/**
 * Xoay 90° / lật ngang để SOI — chỉ đổi cách bày, không ghi gì về máy chủ (spec 2026-10-28 §4.3).
 *
 * Lớp ảnh vẽ bằng CSS `rotate(r) scaleX(-1)` (lật trước, xoay sau). Ống hút, bản đồ nhỏ cần biết
 * điểm đang chỉ trên KHUNG ứng với điểm nào trên ẢNH gốc chưa xoay ⇒ `toImagePoint`.
 */

import type { Size } from "./viewport";

export type Rotation = 0 | 90 | 180 | 270;
export type Orientation = { rotate: Rotation; flip: boolean };
export const UPRIGHT: Orientation = { rotate: 0, flip: false };

export function nextRotation(r: Rotation): Rotation {
  return ((r + 90) % 360) as Rotation;
}

/** Cỡ ảnh sau khi xoay: 90° / 270° đổi chỗ ngang dọc. */
export function rotatedSize(s: Size, o: Orientation): Size {
  return o.rotate === 90 || o.rotate === 270 ? { w: s.h, h: s.w } : s;
}

/** Điểm (u, v) — phần trăm trên ảnh ĐANG BÀY — về điểm trên ảnh chưa xoay, chưa lật. */
export function toImagePoint(u: number, v: number, o: Orientation): { x: number; y: number } {
  let x: number;
  let y: number;
  switch (o.rotate) {
    case 90:
      x = v;
      y = 1 - u;
      break;
    case 180:
      x = 1 - u;
      y = 1 - v;
      break;
    case 270:
      x = 1 - v;
      y = u;
      break;
    default:
      x = u;
      y = v;
  }
  return { x: o.flip ? 1 - x : x, y };
}

export function orientationCss(o: Orientation): string | undefined {
  if (o.rotate === 0 && !o.flip) return undefined;
  return `rotate(${o.rotate}deg)${o.flip ? " scaleX(-1)" : ""}`;
}

/**
 * Kích thước gốc ghi trong CSDL có khi là cỡ cảm biến CHƯA xoay theo cờ EXIF (ảnh dọc lưu ngang).
 * Trình duyệt vẽ theo cờ xoay ⇒ lấy hướng theo ảnh đã tải thật: lệch hướng thì đổi chỗ.
 */
export function orientNatural(natural: Size | undefined, measured: Size | undefined): Size | undefined {
  if (!natural) return undefined;
  if (!measured || measured.w <= 0 || measured.h <= 0) return natural;
  const portraitN = natural.h > natural.w;
  const portraitM = measured.h > measured.w;
  return portraitN === portraitM || natural.w === natural.h ? natural : { w: natural.h, h: natural.w };
}
